import { by639_1 } from 'iso-language-codes'

/**
 * AikenParser
 * Handles legacy AIKEN and Block-based Multilingual AIKEN formats.
 */
class AikenParserService {
    constructor ( config = {} ) {
        this.defaultLang = config.defaultLang || "EN"
        this.startDelimiter = ":: QUESTION_START ::"
        this.endDelimiter = ":: QUESTION_END ::"
    }

    /**
     * Main entry point for validation and parsing
     */
    validateImport ( data, type, question_bank_id, topic_id ) {
        let preview = []
        let finalQuestions = []
        let errors = {}
        let errorLines = []
        let languages = []

        // 1. Generate Preview and Clean Lines
        const rawLines = type === "txt" ? data.split( "\n" ) : data.split( "</p>" )
        rawLines.forEach( ( line, i ) => {
            const displayLine = type === "txt" ? line : `${line.substring( 3 )}</p>`
            preview.push( `<p>${displayLine}</p>` )
        } )

        // 2. Determine Format Path
        const isMultilingual = data.includes( this.startDelimiter )

        if ( isMultilingual ) {
            this._parseMultilingual( rawLines, finalQuestions, errors, errorLines, languages, question_bank_id, topic_id )
            this._verifyTranslationConsistency( finalQuestions, languages, errors, errorLines )
        } else {
            this._parseLegacy( rawLines, finalQuestions, errors, errorLines, question_bank_id, topic_id )
        }

        const validQuestions = finalQuestions.filter( q => q.isValidGroup !== false )

        return { questions: validQuestions, errors, preview, errorLines, languages, raw: data }
    }

    /**
     * Handles the new [EN]...[ES] block logic
     */
    _parseMultilingual ( rawLines, finalQuestions, errors, errorLines, languages, qBankId, topicId ) {
        let currentGroup = null
        let currentLang = null
        let blockContent = []
        let blockStartLine = 0
        let isGroupValid = true

        for ( let i = 0; i < rawLines.length; i++ ) {
            let line = rawLines[i].trim()

            if ( line.includes( this.startDelimiter ) ) {
                isGroupValid = true
                currentGroup = {
                    isMultilingual: true,
                    translations: {},
                    marks: 1,
                    questionType: "MCQ",
                    selectionType: "single",
                    topic_id: topicId,
                    question_bank_id: qBankId,
                    startLine: i + 1
                }
                continue
            }

            // Detect Language Tag [EN], [HI], etc.
            const langMatch = line.match( /^\[([A-Za-z]{2})\]/ )
            if ( langMatch ) {
                // If we were already tracking a language, parse it before moving to the next
                if ( currentLang && blockContent.length > 0 ) {
                    const validQuestion = this._processBlock( currentLang, blockContent, blockStartLine, currentGroup, errors, errorLines, languages )
                    isGroupValid = isGroupValid && validQuestion
                }

                currentLang = langMatch[1].toLowerCase()

                const languageDetails = by639_1[currentLang]
                if ( !languageDetails ) {
                    if ( !errors[i + 1] ) {
                        errors[i + 1] = { line: i + 1, messages: [] }
                        errorLines.push( i + 1 )
                    }
                    errors[i + 1].messages.push( "Invalid language code detected! Please use ISO language codes." )
                }

                blockStartLine = i
                blockContent = [line.replace( langMatch[0], "" ).trim()] // Start content without the tag
                continue
            }

            if ( line.includes( this.endDelimiter ) ) {
                if ( currentLang && blockContent.length > 0 ) {
                    const validQuestion = this._processBlock( currentLang, blockContent, blockStartLine, currentGroup, errors, errorLines, languages )
                    isGroupValid = isGroupValid && validQuestion
                }
                if ( isGroupValid && currentGroup && Object.keys( currentGroup.translations ).length > 0 ) {
                    currentGroup.endLine = i + 1
                    finalQuestions.push( currentGroup )
                }
                currentGroup = null
                currentLang = null
                blockContent = []
                continue
            }

            // Accumulate lines for the current language block
            if ( currentLang ) {
                blockContent.push( line )
            }
        }
    }

    // Helper to parse the accumulated block and handle errors with correct line numbers
    _processBlock ( lang, content, startLine, group, errors, errorLines, languages ) {
        if ( !languages.includes( lang ) ) languages.push( lang )

        if ( group.translations[lang] ) {

            const errorLine = startLine + 1

            if ( !errors[errorLine] ) {
                errors[errorLine] = { line: errorLine, messages: [] }
                errorLines.push( errorLine )
            }
            errors[errorLine].messages.push( "Duplicate language code detected." )
            return false
        }


        const { data, blockErrors } = this._innerAikenParser( content, startLine, lang )

        if ( blockErrors.length > 0 ) {
            blockErrors.forEach( err => {
                if ( !errors[err.line] ) {
                    errors[err.line] = { line: err.line, messages: [] }
                    errorLines.push( err.line )
                }
                errors[err.line].messages.push( err.message )
            } )
            return false // Signal that this block is invalid
        }


        // Assign base data if it's the first successful block
        if ( languages.at( 0 ) === lang ) {
            group.question = data.question
            group.options = data.options
            group.correctAnswer = data.correctAnswer
            group.answersStatus = data.answersStatus
            group.translations[lang] = null
        } else {
            group.translations[lang] = data
        }

        return true
    }

    /**
     * Standard AIKEN logic (Matches your previous logic exactly)
     */
    _parseLegacy ( lines, finalQuestions, errors, errorLines, qBankId, topicId ) {
        let question = null
        let options = []
        let answers = []
        let answersStatus = []
        let correctAnswer = null

        for ( let i = 0; i < lines.length; i++ ) {
            let line = lines[i].trim()

            if ( line === "" || line === "\r" || line === "<br />" ) {
                this._handleErrorCheck( question, options, correctAnswer, i, errors, errorLines )
                question = null; options = []; answers = []; answersStatus = []; correctAnswer = null
            } else if ( line.match( /^([A-Z][.)]\s)/ ) || line.match( /^(<p>[A-Z][.)]\s)/ ) ) {
                const splitted = line.replace( "<p>", "" ).split( /^([A-Z][.)]\s)/ )
                options.push( { option_text: splitted[2] } )
                answers.push( splitted[1][0] )
                answersStatus.push( false )
            } else if ( line.includes( "ANSWER: " ) ) {
                const answerLetter = line.split( "ANSWER: " )[1].trim()[0]
                correctAnswer = answers.indexOf( answerLetter )

                if ( correctAnswer !== -1 ) answersStatus[correctAnswer] = true

                if ( question && correctAnswer !== null && correctAnswer >= 0 && options.length > 1 ) {
                    finalQuestions.push( {
                        translations: null,
                        question,
                        options,
                        correctAnswer,
                        answersStatus,
                        isMultilingual: false,
                        marks: 1,
                        questionType: "MCQ",
                        selectionType: "single",
                        topic_id: topicId,
                        question_bank_id: qBankId
                    } )
                } else {
                    this._logDetailedError( question, options, correctAnswer, i, errors, errorLines )
                }
                question = null; options = []; answers = []; answersStatus = []; correctAnswer = null
            } else {
                question = line
            }
        }
    }

    /**
     * Reusable logic for parsing a single block of AIKEN text
     */
    _innerAikenParser ( lines, offset, lang ) {
        let question = null
        let options = []
        let answers = []
        let answersStatus = []
        let correctAnswer = null
        let blockErrors = []
        let lastOptionLine = offset

        const prefix = `[${lang}] `

        // 1. Initial Check for empty block
        if ( lines.length === 0 ) {
            blockErrors.push( {
                line: offset + 1,
                message: `${prefix}- Language block is empty.`
            } )
        }

        for ( let i = 0; i < lines.length; i++ ) {
            let line = lines[i].trim()
            const absoluteLine = offset + i + 1

            if ( line.match( /^([A-Z][.)]\s)/ ) ) {
                const splitted = line.split( /^([A-Z][.)]\s)/ )
                options.push( { option_text: splitted[2] } )
                answers.push( splitted[1][0] )
                answersStatus.push( false )
                lastOptionLine = absoluteLine
            } else if ( line.includes( "ANSWER: " ) ) {
                const letter = line.split( "ANSWER: " )[1].trim()[0]
                correctAnswer = answers.indexOf( letter )
                if ( correctAnswer !== -1 ) {
                    answersStatus[correctAnswer] = true
                } else {
                    blockErrors.push( {
                        line: absoluteLine,
                        message: `${prefix}- Correct answer letter does not match any options.`
                    } )
                }
            } else if ( line !== "" ) {
                if ( question === null ) {
                    question = line
                }
            }
        }

        if ( !question ) {
            blockErrors.push( { line: offset + 1, message: `${prefix}- Question text is missing.` } )
        }
        if ( options.length < 2 ) {
            blockErrors.push( { line: offset + 2, message: `${prefix}- At least two options are required.` } )
        }
        if ( correctAnswer === null ) {
            blockErrors.push( { line: lastOptionLine + 1, message: `${prefix}- "ANSWER: " line is missing.` } )
        }

        return {
            data: { question, options, correctAnswer, answersStatus },
            blockErrors
        }
    }

    _handleErrorCheck ( q, opt, ans, i, errors, errorLines ) {
        if ( q && q.trim() !== "" && ( opt.length > 0 || ans !== null ) ) {
            errors[i + 1] = { line: i + 1, messages: ["- Answer or options not provided"] }
            errorLines.push( i + 1 )
        }
    }

    _logDetailedError ( q, opt, ans, i, errors, errorLines, lang = null ) {
        let messages = []
        const prefix = lang ? `[${lang}] ` : ""

        if ( !q || q.trim() === "" )
            messages.push( `${prefix}- Question is not provided.` )

        if ( ans === null || ans < 0 )
            messages.push( `${prefix}- Correct answer is not provided (Check "ANSWER: " line).` )

        if ( !opt || opt.length === 0 )
            messages.push( `${prefix}- Options not provided.` )
        else if ( opt.length === 1 )
            messages.push( `${prefix}- At least two options are required.` )

        // i + 1 matches your legacy line numbering logic
        errors[i + 1] = { line: i + 1, messages }
        errorLines.push( i + 1 )
    }

    _verifyTranslationConsistency ( finalQuestions, allLanguages, errors, errorLines ) {
        finalQuestions.forEach( ( group, index ) => {
            const groupLangs = Object.keys( group.translations )
            const missingLangs = allLanguages.filter( lang => !groupLangs.includes( lang ) )

            if ( missingLangs.length > 0 ) {
                // Mark as invalid so it's not imported
                group.isValidGroup = false

                // We need a line to pin this error to. 
                // We'll use the line where this specific question block starts.
                const errorLine = group.endLine || 1

                if ( !errors[errorLine] ) {
                    errors[errorLine] = { line: errorLine, messages: [] }
                    errorLines.push( errorLine )
                }

                errors[errorLine].messages.push(
                    `Missing translations: This question group is missing [${missingLangs.join( ", " )}].`
                )
            }
        } )
    }
}

const AikenParser = new AikenParserService()

export default AikenParser